import { useState } from 'react';
import type { LifecycleAbuseScenario, LifecycleAnalysisItem } from '../types/lifecycle';
import { AnalysisReason } from './AnalysisReason';
import { EventsTimeline } from './EventsTimeline';
import {
  formatDateTime,
  humanizePublicText,
  trackingLevelClass,
  trackingLevelLabel,
} from '../utils/lifecycleFormat';
import { democraticBandLabel } from '../utils/democraticBand';
import { RiderBadge } from './RiderBadge';
import { RiderExplanationPanel } from './RiderExplanationPanel';
import { LegislativePath, LegislativeStageBadge } from './LegislativePath';
import { SequenceBadge, SequencePanel, ScoreBreakdownPanel } from './SequenceSurfacing';
import { InstitutionalWarningBadge, InstitutionalWarningPanel } from './InstitutionalWarning';

interface LifecycleAnalysisDetailProps {
  item: LifecycleAnalysisItem;
  onClose: () => void;
}

function formatAbuseScenario(scenario: string | LifecycleAbuseScenario, index: number): {
  title: string;
  likelihood?: string;
  mechanism?: string;
  triggerConditions?: string;
  actors?: string;
  safeguards?: string;
} {
  if (typeof scenario === 'string') {
    return { title: scenario };
  }
  return {
    title: scenario.scenario_title || `Scénář ${index + 1}`,
    likelihood: scenario.realistic_likelihood,
    mechanism: scenario.mechanism || scenario.textual_evidence || '',
    triggerConditions: scenario.trigger_conditions || scenario.why_not_just_theoretical || '',
    actors: [scenario.who_could_misuse, scenario.against_whom].filter(Boolean).join(' → '),
    safeguards: scenario.what_would_block_it || '',
  };
}

export function LifecycleAnalysisDetail({ item, onClose }: LifecycleAnalysisDetailProps) {
  const isAlert = item.alert === 'yes';
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const confidencePct = item.confidence !== null ? Math.round(item.confidence * 100) : null;
  const hasRiskScore = item.risk_score !== null && item.risk_level !== null;

  async function copyLink() {
    if (typeof window === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 1800);
    } catch {
      setCopyStatus('failed');
      window.setTimeout(() => setCopyStatus('idle'), 1800);
    }
  }

  return (
    <aside className="detail-panel detail-panel--analysis" aria-label="Detail analýzy">
      <div className="detail-panel__header">
        <div className="detail-panel__badges">
          <span className={`badge badge--risk ${trackingLevelClass(item.final_risk_level)}`}>
            {trackingLevelLabel(item.final_risk_level)}
          </span>
          {isAlert && <span className="badge badge--alert" aria-label="Upozornění">⚠ Upozornění</span>}
          {item.possible_rider?.label && <RiderBadge label={item.possible_rider.label} />}
          {item.sequence_surfacing && <SequenceBadge surfacing={item.sequence_surfacing} />}
          <InstitutionalWarningBadge context={item.institutional_context} />
          {item.constitutional_relevance && (
            <span className="badge badge--constitutional">
              ústavní význam: {trackingLevelLabel(item.constitutional_relevance)}
            </span>
          )}
          <LegislativeStageBadge status={item.legislative_status} fallbackLabel={null} />
        </div>
        <div className="detail-panel__actions">
          <button type="button" className="detail-panel__copy" onClick={copyLink}>
            Kopírovat odkaz
          </button>
          <button type="button" className="detail-panel__close" onClick={onClose} aria-label="Zavřít detail">×</button>
        </div>
      </div>

      <h2 className="detail-panel__title">{item.title}</h2>
      <div className="detail-panel__source">
        {item.detail_url ? (
          <a href={item.detail_url} target="_blank" rel="noopener noreferrer">Zdroj: PSP — sněmovní tisk</a>
        ) : (
          <span>Zdroj: PSP — sněmovní tisk není k dispozici</span>
        )}
        {item.completed_at && <span>Aktualizováno: {formatDateTime(item.completed_at)}</span>}
        {copyStatus === 'copied' && <span className="detail-panel__copy-status">Odkaz zkopírován</span>}
        {copyStatus === 'failed' && <span className="detail-panel__copy-status">Kopírování se nepovedlo</span>}
      </div>

      {/* AnalysisReason MUSI byt jako prvni sekce po headeru per spec */}
      <AnalysisReason explanation={item.lifecycle_trigger_explanation} />

      <EventsTimeline events={item.events_timeline} />

      {item.possible_rider && <RiderExplanationPanel possibleRider={item.possible_rider} />}

      <LegislativePath
        status={item.legislative_status}
        transition={item.events_timeline.find((e) => e.transition)?.transition ?? null}
      />

      <SequencePanel surfacing={item.sequence_surfacing} />

      <InstitutionalWarningPanel context={item.institutional_context} />

      {item.score_breakdown && <ScoreBreakdownPanel breakdown={item.score_breakdown} />}

      <dl className="detail-panel__facts">
        <dt>ID návrhu</dt>
        <dd><code>{item.proposal_id}</code></dd>
        {hasRiskScore && (
          <>
            <dt>Riziko (AI verdikt)</dt>
            <dd>
              <strong>{item.risk_score}</strong> / 100 {' '}<span className="democratic-band-chip">{democraticBandLabel(item.risk_score ?? 0)}</span>
              {' · '}
              <span className={`badge badge--risk ${trackingLevelClass(item.risk_level)}`}>{trackingLevelLabel(item.risk_level)}</span>
              {item.risk_assessed_at && (
                <div className="detail-panel__fact-hint">Posouzeno {formatDateTime(item.risk_assessed_at)}.</div>
              )}
            </dd>
          </>
        )}
        {item.tracking_score !== null && (
          <>
            <dt>Priorita sledování</dt>
            <dd><strong>{item.tracking_score}</strong> / 100</dd>
          </>
        )}
        {confidencePct !== null && (
          <>
            <dt>Jistota posouzení</dt>
            <dd>
              <div className="confidence-meter" aria-label={`Jistota posouzení ${confidencePct} procent`}>
                <div className="confidence-meter__bar">
                  <div className="confidence-meter__fill" style={{ width: `${confidencePct}%` }} />
                </div>
                <strong>{confidencePct} %</strong>
              </div>
              <small className="muted">
                Vyjadřuje sílu dostupných podkladů a konzistenci analýzy, ne pravděpodobnost, že je návrh škodlivý.
              </small>
            </dd>
          </>
        )}
        {item.completed_at && (
          <>
            <dt>Dokončeno</dt>
            <dd>{formatDateTime(item.completed_at)}</dd>
          </>
        )}
      </dl>

      {item.summary && (
        <section className="detail-panel__section detail-panel__section--summary">
          <h4><span aria-hidden="true">●</span> Shrnutí</h4>
          <p>{humanizePublicText(item.summary)}</p>
        </section>
      )}

      {item.plain_explanation && (
        <section className="detail-panel__section">
          <h4><span aria-hidden="true">●</span> Jednoduchý výklad</h4>
          <p>{humanizePublicText(item.plain_explanation)}</p>
        </section>
      )}

      {item.key_mechanisms.length > 0 && (
        <section className="detail-panel__section">
          <h4><span aria-hidden="true">●</span> Co je v návrhu podstatné</h4>
          <ul>
            {item.key_mechanisms.map((m, i) => <li key={i}>{humanizePublicText(m)}</li>)}
          </ul>
        </section>
      )}

      {item.democratic_impact_areas.length > 0 && (
        <section className="detail-panel__section">
          <h4><span aria-hidden="true">●</span> Oblasti dopadu</h4>
          <ul className="detail-panel__chips">
            {item.democratic_impact_areas.map((a, i) => <li key={i} className="chip"><code>{a}</code></li>)}
          </ul>
        </section>
      )}

      {item.abuse_scenarios.length > 0 && (
        <section className="detail-panel__section detail-panel__section--abuse">
          <h4><span aria-hidden="true">●</span> Scenare a slaba mista k overeni</h4>
          <div className="abuse-scenarios">
            {item.abuse_scenarios.map((s, i) => {
              const scenario = formatAbuseScenario(s, i);
              return (
                <article className="abuse-scenario" key={`${scenario.title}-${i}`}>
                  <div className="abuse-scenario__header">
                    <h5>{humanizePublicText(scenario.title)}</h5>
                    {scenario.likelihood && (
                      <span className="abuse-scenario__likelihood">
                        pravděpodobnost: {humanizePublicText(scenario.likelihood)}
                      </span>
                    )}
                  </div>
                  {scenario.mechanism && (
                    <p>{humanizePublicText(scenario.mechanism)}</p>
                  )}
                  {scenario.triggerConditions && (
                    <p>{humanizePublicText(scenario.triggerConditions)}</p>
                  )}
                  {scenario.actors && (
                    <p className="abuse-scenario__meta">{humanizePublicText(scenario.actors)}</p>
                  )}
                  {scenario.safeguards && (
                    <p className="abuse-scenario__meta">{humanizePublicText(scenario.safeguards)}</p>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </aside>
  );
}
